import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { capture } from '@/lib/analytics';
import {
  Rocket, Download, Copy, Globe, CheckCircle2,
  ExternalLink, Loader2, Link2, RefreshCw,
  ChevronDown, ChevronUp, AlertCircle, Zap, ShoppingCart,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const ORBIS_DOMAIN = 'orbis.build';

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'my-site';
}

// ── Small UI atoms ────────────────────────────────────────────────────────────

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className={`rounded-md p-1.5 transition-colors hover:bg-[var(--orbis-s2)] ${className}`}
      style={{ color: 'var(--orbis-t3)' }}
      title="Copy"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--orbis-positive)' }} /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function LiveUrlRow({ url }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5 rounded-lg w-full" style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.15)' }}>
      <div className="flex items-center gap-2 min-w-0">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--orbis-positive)' }} />
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="text-xs truncate hover:underline" style={{ color: 'var(--orbis-positive)', fontFamily: 'var(--orbis-mono)' }}>
          {url}
        </a>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        <CopyButton text={url} />
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="rounded-md p-1.5 transition-colors hover:bg-[var(--orbis-s2)]"
          style={{ color: 'var(--orbis-t3)' }}>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>
      {children}
    </span>
  );
}

function Divider() {
  return <div className="w-full h-px" style={{ background: 'var(--orbis-border)' }} />;
}

// ── DNS instructions card ─────────────────────────────────────────────────────

function DnsCard({ instructions, domain }) {
  const records = instructions?.records || [];
  return (
    <div className="w-full rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--orbis-amber)' }} />
        <p style={{ fontSize: 12, color: 'var(--orbis-t2)', lineHeight: 1.5 }}>
          Add the following DNS record(s) for <strong style={{ color: 'var(--orbis-t1)' }}>{domain}</strong> in your DNS provider:
        </p>
      </div>
      {records.map((r, i) => (
        <div key={i} className="rounded-md overflow-hidden" style={{ border: '1px solid var(--orbis-s3)' }}>
          <div className="grid text-xs" style={{ gridTemplateColumns: '60px 1fr 1fr', background: 'var(--orbis-s1)' }}>
            {['Type', 'Name', 'Value'].map(h => (
              <div key={h} className="px-3 py-1.5" style={{ color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--orbis-s3)' }}>{h}</div>
            ))}
            <div className="px-3 py-2" style={{ color: 'var(--orbis-blue)', fontFamily: 'var(--orbis-mono)' }}>{r.type}</div>
            <div className="px-3 py-2" style={{ color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>{r.name}</div>
            <div className="px-3 py-2 flex items-center gap-1" style={{ color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}>
              <span className="truncate">{r.value}</span>
              <CopyButton text={r.value} />
            </div>
          </div>
        </div>
      ))}
      {instructions?.note && (
        <p style={{ fontSize: 11, color: 'var(--orbis-t3)', lineHeight: 1.5 }}>{instructions.note}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function DeployStage({ project, onDownload, onDeploy, onEditSite }) {
  const projectId = project?.id;
  const ms = project?.magic_state || {};

  // ── State ──────────────────────────────────────────────────────────────────
  const [slug, setSlug]                   = useState('');
  const [slugStatus, setSlugStatus]       = useState('idle'); // idle | checking | available | taken
  const [publishing, setPublishing]       = useState(false);
  const [publishedUrl, setPublishedUrl]   = useState(ms.published_url || '');
  const [currentSlug, setCurrentSlug]     = useState(project?.subdomain_slug || '');

  // Custom domain section
  const [showCustomDomain, setShowCustomDomain] = useState(false);
  const [customDomain, setCustomDomain]   = useState(project?.custom_domain || '');
  const [connectingDomain, setConnectingDomain] = useState(false);
  const [connectedDomain, setConnectedDomain]   = useState(project?.custom_domain || '');
  const [dnsInstructions, setDnsInstructions]   = useState(null);
  const [verifyingDns, setVerifyingDns]   = useState(false);
  const [dnsVerified, setDnsVerified]     = useState(false);

  const navigate = useNavigate();
  const isPublished = !!publishedUrl || !!currentSlug;

  // Auto-generate slug from project name on mount
  useEffect(() => {
    if (!currentSlug) {
      const auto = slugify(project?.name || 'my-site');
      setSlug(auto);
    } else {
      setSlug(currentSlug);
    }
  }, [project?.name, currentSlug]);

  // ── Slug check (debounced) ─────────────────────────────────────────────────
  useEffect(() => {
    if (!slug || slug === currentSlug) { setSlugStatus('idle'); return; }
    setSlugStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/deploy/check-slug`, { params: { slug } });
        setSlugStatus(data.available ? 'available' : 'taken');
      } catch { setSlugStatus('idle'); }
    }, 500);
    return () => clearTimeout(timer);
  }, [slug, currentSlug]);

  // ── Publish ────────────────────────────────────────────────────────────────
  const handlePublish = useCallback(async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API}/api/deploy/publish`, {
        project_id: projectId,
        slug: slug || undefined,
      });
      setPublishedUrl(data.url);
      setCurrentSlug(data.slug);
      setSlug(data.slug);
      setSlugStatus('idle');
      capture('site_published', { project_id: projectId, slug: data.slug, url: data.url });
      toast.success('Site is live! 🚀');
      if (onDeploy) onDeploy();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  }, [projectId, slug, publishing, onDeploy]);

  // ── Connect custom domain ──────────────────────────────────────────────────
  const handleConnectDomain = useCallback(async () => {
    if (!customDomain || connectingDomain) return;
    setConnectingDomain(true);
    try {
      const { data } = await axios.post(`${API}/api/deploy/custom-domain`, {
        project_id: projectId,
        domain: customDomain,
      });
      setConnectedDomain(data.domain);
      setDnsInstructions(data.dns_instructions);
      capture('custom_domain_connected', { project_id: projectId, domain: data.domain });
      toast.success('Domain registered — add the DNS record below');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to connect domain');
    } finally {
      setConnectingDomain(false);
    }
  }, [projectId, customDomain, connectingDomain]);

  // ── Verify DNS ─────────────────────────────────────────────────────────────
  const handleVerifyDns = useCallback(async () => {
    if (!connectedDomain || verifyingDns) return;
    setVerifyingDns(true);
    try {
      const { data } = await axios.post(`${API}/api/deploy/verify-domain`, {
        project_id: projectId,
        domain: connectedDomain,
      });
      if (data.verified) {
        setDnsVerified(true);
        toast.success('Domain verified ✓');
      } else {
        toast.info(data.message || 'DNS not yet propagated — try again in a few minutes');
      }
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setVerifyingDns(false);
    }
  }, [projectId, connectedDomain, verifyingDns]);

  // ── Slug input decoration ─────────────────────────────────────────────────
  const slugBorderColor = {
    idle:      'var(--orbis-s2)',
    checking:  'var(--orbis-s3)',
    available: 'rgba(52,211,153,0.4)',
    taken:     'rgba(239,68,68,0.4)',
  }[slugStatus];

  const slugHint = {
    idle:      null,
    checking:  '…',
    available: `✓ ${slug}.${ORBIS_DOMAIN} is available`,
    taken:     `✗ ${slug}.${ORBIS_DOMAIN} is taken`,
  }[slugStatus];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center justify-center"
      style={{ minHeight: 'calc(100vh - 64px)', background: 'var(--orbis-bg)', padding: '40px 24px' }}>

      <div className="w-full max-w-[540px] flex flex-col gap-4">

        {/* Header */}
        <div className="text-center mb-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
            style={{ background: isPublished ? 'rgba(52,211,153,0.12)' : 'rgba(184,204,223,0.1)', border: `1px solid ${isPublished ? 'rgba(52,211,153,0.2)' : 'rgba(184,204,223,0.15)'}` }}>
            {isPublished
              ? <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--orbis-positive)' }} />
              : <Rocket className="w-6 h-6" style={{ color: 'var(--orbis-blue)' }} />}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 300, color: 'var(--orbis-t1)', marginBottom: 6 }}>
            {isPublished ? 'Your site is live' : 'Publish your site'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--orbis-t3)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto' }}>
            {isPublished
              ? 'Your website is live on the Orbis network. Share it or connect your own domain below.'
              : 'Choose a subdomain and publish your site to the Orbis network — free, global CDN, instant.'}
          </p>
        </div>

        {/* ── Orbis subdomain card ── */}
        <div className="rounded-xl p-5 flex flex-col gap-4" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>

          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" style={{ color: 'var(--orbis-blue)' }} />
            <SectionLabel>Orbis Subdomain</SectionLabel>
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,211,153,0.1)', color: 'var(--orbis-positive)', fontFamily: 'var(--orbis-mono)', fontSize: 10 }}>FREE</span>
          </div>

          {/* Slug input */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-stretch h-11 rounded-lg overflow-hidden w-full"
              style={{ border: `1px solid ${slugBorderColor}`, transition: 'border-color 0.15s' }}>
              <div className="flex items-center px-3 shrink-0" style={{ background: 'var(--orbis-s2)', borderRight: '1px solid var(--orbis-s3)' }}>
                <Globe className="w-3.5 h-3.5" style={{ color: 'var(--orbis-t4)' }} />
              </div>
              <input
                type="text"
                value={slug}
                onChange={e => setSlug(slugify(e.target.value))}
                disabled={publishing || (isPublished && slug === currentSlug)}
                className="flex-1 bg-transparent outline-none px-3"
                style={{ fontSize: 13, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-mono)' }}
                placeholder="your-site"
                spellCheck={false}
              />
              <div className="flex items-center px-3 shrink-0" style={{ borderLeft: '1px solid var(--orbis-s3)' }}>
                <span style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', whiteSpace: 'nowrap' }}>
                  .{ORBIS_DOMAIN}
                </span>
              </div>
            </div>
            {slugHint && (
              <p style={{ fontSize: 11, color: slugStatus === 'available' ? 'var(--orbis-positive)' : slugStatus === 'taken' ? '#EF4444' : 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)' }}>
                {slugHint}
              </p>
            )}
          </div>

          {/* Live URL or publish button */}
          {isPublished ? (
            <div className="flex flex-col gap-2">
              <LiveUrlRow url={publishedUrl || `https://${currentSlug}.${ORBIS_DOMAIN}`} />
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center justify-center gap-2 w-full rounded-lg h-9 text-xs transition-colors"
                style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t2)', border: '1px solid var(--orbis-s3)', cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.5 : 1 }}
              >
                {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {publishing ? 'Republishing…' : 'Republish (push latest changes)'}
              </button>
            </div>
          ) : (
            <button
              onClick={handlePublish}
              disabled={publishing || slugStatus === 'taken'}
              className="orbis-cta w-full flex items-center justify-center gap-2 rounded-full transition-all h-11"
              style={{ fontSize: 13, fontWeight: 500, opacity: (publishing || slugStatus === 'taken') ? 0.5 : 1, cursor: (publishing || slugStatus === 'taken') ? 'not-allowed' : 'pointer' }}
            >
              {publishing
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</>
                : <><Rocket className="w-4 h-4" /> Publish to Orbis</>}
            </button>
          )}
        </div>

        {/* ── Custom domain card ── */}
        {isPublished && (
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
            {/* Collapsible header */}
            <button
              onClick={() => setShowCustomDomain(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 transition-colors hover:bg-[var(--orbis-s2)]"
            >
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4" style={{ color: connectedDomain ? 'var(--orbis-positive)' : 'var(--orbis-t3)' }} />
                <SectionLabel>Custom Domain</SectionLabel>
                {connectedDomain && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: dnsVerified ? 'rgba(52,211,153,0.1)' : 'rgba(212,184,150,0.1)', color: dnsVerified ? 'var(--orbis-positive)' : 'var(--orbis-amber)', fontFamily: 'var(--orbis-mono)', fontSize: 10 }}>
                    {dnsVerified ? '✓ verified' : 'pending DNS'}
                  </span>
                )}
              </div>
              {showCustomDomain ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--orbis-t4)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--orbis-t4)' }} />}
            </button>

            {showCustomDomain && (
              <div className="px-5 pb-5 flex flex-col gap-4" style={{ borderTop: '1px solid var(--orbis-border)' }}>
                <p className="pt-4" style={{ fontSize: 12, color: 'var(--orbis-t3)', lineHeight: 1.6 }}>
                  Connect your own domain (e.g. <span style={{ fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t2)' }}>www.acme.com</span>). We'll give you the DNS record to add.
                </p>

                {/* Domain input + connect */}
                <div className="flex gap-2">
                  <div className="flex items-center flex-1 h-10 rounded-lg px-3 gap-2"
                    style={{ background: 'var(--orbis-bg)', border: '1px solid var(--orbis-s3)' }}>
                    <Globe className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--orbis-t4)' }} />
                    <input
                      type="text"
                      value={customDomain}
                      onChange={e => setCustomDomain(e.target.value)}
                      placeholder="www.yourdomain.com"
                      className="flex-1 bg-transparent outline-none"
                      style={{ fontSize: 13, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}
                    />
                  </div>
                  <button
                    onClick={handleConnectDomain}
                    disabled={!customDomain || connectingDomain}
                    className="h-10 px-4 rounded-lg text-xs font-medium transition-colors shrink-0"
                    style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t1)', border: '1px solid var(--orbis-s3)', opacity: (!customDomain || connectingDomain) ? 0.4 : 1, cursor: (!customDomain || connectingDomain) ? 'not-allowed' : 'pointer' }}
                  >
                    {connectingDomain ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect'}
                  </button>
                </div>

                {/* Connected domain URL */}
                {connectedDomain && !dnsVerified && (
                  <>
                    <LiveUrlRow url={`https://${connectedDomain}`} />
                    {dnsInstructions && <DnsCard instructions={dnsInstructions} domain={connectedDomain} />}
                    <button
                      onClick={handleVerifyDns}
                      disabled={verifyingDns}
                      className="flex items-center justify-center gap-2 w-full rounded-lg h-9 text-xs transition-colors"
                      style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t2)', border: '1px solid var(--orbis-s3)', opacity: verifyingDns ? 0.5 : 1, cursor: verifyingDns ? 'not-allowed' : 'pointer' }}
                    >
                      {verifyingDns ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Check DNS Propagation
                    </button>
                  </>
                )}

                {/* Verified */}
                {connectedDomain && dnsVerified && (
                  <LiveUrlRow url={`https://${connectedDomain}`} />
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Edit Site (post-publish) ── */}
        {isPublished && onEditSite && (
          <>
            <Divider />
            <div className="rounded-xl p-4 flex items-center justify-between gap-3"
              style={{ background: 'rgba(184,204,223,0.05)', border: '1px solid rgba(184,204,223,0.1)' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--orbis-t1)', marginBottom: 2 }}>
                  Want to make changes?
                </p>
                <p style={{ fontSize: 11, color: 'var(--orbis-t4)', lineHeight: 1.5 }}>
                  Go back to the editor, make your edits, then republish.
                </p>
              </div>
              <button
                onClick={onEditSite}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium shrink-0 transition-colors"
                style={{ background: 'rgba(184,204,223,0.12)', color: 'var(--orbis-blue)', border: '1px solid rgba(184,204,223,0.2)' }}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Edit Site
              </button>
            </div>
          </>
        )}

        {/* ── Download ZIP ── */}
        <Divider />
        <button
          onClick={onDownload}
          className="w-full flex items-center justify-center gap-2 rounded-full h-11 transition-colors"
          style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)', fontSize: 13, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}
        >
          <Download className="w-4 h-4" />
          Download ZIP
        </button>

        {/* ── Cost note ── */}
        <p className="text-center" style={{ fontSize: 11, color: 'var(--orbis-t4)', lineHeight: 1.5 }}>
          Hosted on Cloudflare's global network · Free subdomain included · Custom domain $0/mo
        </p>

        {/* ── Domain upsell ── */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl"
          style={{ background: 'rgba(184,204,223,0.05)', border: '1px solid rgba(184,204,223,0.1)' }}>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-blue)' }} />
            <div>
              <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t2)' }}>Want your own domain?</p>
              <p style={{ fontSize: 11, color: 'var(--orbis-t4)' }}>From $12.99/yr · WHOIS privacy included</p>
            </div>
          </div>
          <a
            href="/domains"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 no-underline"
            style={{ background: 'rgba(184,204,223,0.12)', color: 'var(--orbis-blue)', border: '1px solid rgba(184,204,223,0.2)' }}>
            <ShoppingCart className="w-3 h-3" />
            Get a domain
          </a>
        </div>

      </div>
    </div>
  );
}
